/**
 * Emotes — E6 (`design/economy/economy-rebuild.md` §4.4).
 *
 * ## What an emote is here, and why it is not a dance
 *
 * The design doc said "new animations on the existing rig". There are none, and there is no
 * way to author them: read straight off the shipped GLBs, the two rig families share exactly
 * three expressive clips — `Wave`, a punch, and a slash. Three clips cannot carry twelve SKUs,
 * and giving four SKUs the same body pose would be selling the same thing four times.
 *
 * So an emote in Black Line is a **radio callout**: a positional voice line, a short bubble over
 * the player's head, and — on the three where a rig actually has the clip — a body pose to go
 * with it. That is the idiom of the genre this game is in (a tactical shooter's players call
 * contact, not dance), it is buildable with the VO pipeline already in the repo
 * (`tools/sfx/manifest.json` + `import-generated.mjs`), and every SKU is genuinely distinct
 * because the line is.
 *
 * ## Inert, which is the load-bearing requirement
 *
 * §4.4: "Must be inert: no camera gain, no hitbox change, no peeking". A callout cannot move a
 * camera or a capsule because it never touches the movement state — the server broadcasts an
 * event and the client plays a sound and draws a bubble. The three body clips are played on the
 * RENDERED figure only, never on the collision capsule, exactly like the crouch scale already
 * is a drawn-body concern with `hitCapsule()` deriving from `MOVEMENT.crouchHeight` separately.
 *
 * `EMOTE_LIVE_ROUND_POLICY` states the competitive gate in one place so the server and the
 * client's own "this key does nothing right now" hint cannot disagree about it.
 *
 * ## The wire cost, stated
 *
 * Upstream: zero — bit 15 of the `buttons` field that was already on the wire. Downstream: one
 * `MSG.EMOTE` per trigger, entity + SKU, two bytes (`emote-codec.ts`), rate limited server-side
 * to one per `EMOTE_COOLDOWN_MS`. The equipped id is NOT join state: the event carries it, so a
 * late joiner needs nothing and the roster stays the size it is.
 *
 * That is not in tension with the `emote` claim on the join ticket, which is a different
 * question with a different answer. The ticket tells the SERVER which SKU this player owns, and
 * it has to be signed for the same reason the charm is — a client that named its own would be
 * using one it never bought. The event tells every OTHER client what to play, and it carries the
 * id so that nobody has to have been in the room when the speaker joined. One fact, signed once
 * on the way in; one event, broadcast when it is used.
 *
 * ## Ids
 *
 * `0` is `NO_EMOTE`. Real emotes are `1..12`, stable once shipped — an `emote:<id>` `UserItem`
 * row persists them.
 */

export const NO_EMOTE = 0;

/** Minimum gap between one player's emotes. Same reasoning as the spray's, with a shorter
 * floor because an emote leaves nothing behind to accumulate. */
export const EMOTE_COOLDOWN_MS = 3_000;

/**
 * Whether an emote is allowed while a competitive round is live.
 *
 * `false` — refused while the round runs, allowed in warmup, freeze time and at round end. The
 * spec left this open between "refused while live" and "round end only"; they are the same rule
 * stated from two directions, and this is the one that closes the whole exploit class rather
 * than arguing about which second of a round is safe. Deathmatch has no live-round concept and
 * allows them throughout.
 */
export const EMOTE_LIVE_ROUND_POLICY = false;

/** Ids are stable and must never be reused — an `emote:<id>` `UserItem` row persists them. */
export const EMOTE = {
  // ── The callouts a squad actually needs. Priced lowest because they are the useful ones and
  // a player who buys nothing else should be able to afford one.
  contact: 1,
  needBackup: 2,
  covering: 3,
  onMe: 4,
  regroup: 5,
  holdingHere: 6,
  // ── The social half, ascending. Nothing here is information — it is what a player says when
  // the round is already decided, which is exactly why it costs more.
  niceShot: 7,
  myBad: 8,
  goodGame: 9,
  affirmative: 10,
  standDown: 11,
  blackLine: 12,
} as const;

export type EmoteId = (typeof EMOTE)[keyof typeof EMOTE];

export interface EmoteDefinition {
  id: number;
  /** Shop and wheel label. */
  name: string;
  /** What the operator actually says — rendered in the bubble over their head, and the script
   * the voice line is generated from, so the two can never drift. */
  line: string;
  description: string;
  /**
   * Clip names to try on the rendered figure, in order, or an empty list for a callout with no
   * body pose. Resolved through the same `firstAvailable` walk `resolveClips` already uses for
   * locomotion, because the two rig families name their clips differently and neither ships
   * everything: tactical is `CharacterArmature|`-prefixed, survivor is bare.
   */
  clips: readonly string[];
  /** Key of the imported voice line — see `emoteVoicePath`. */
  voice: string;
  /**
   * File stem of this callout's glyph — `/game/ui/emotes/<art>.png`.
   *
   * Twelve SKUs shared one megaphone icon until 2026-09-16, which made a shop of twelve
   * identical rows. A callout's glyph is what it MEANS — crossed flags for "need backup", an
   * anchor for "holding here" — so it is content, not decoration, and it lives beside the line
   * it belongs to.
   */
  art: string;
  priceCoins: number;
  currency: 'PREMIUM';
}

/** The only body clips both rig families actually ship, read off the GLBs rather than assumed.
 * Anything not in one of these lists resolves to no pose at all, which is a callout that is
 * only heard — never a wrong pose standing in for a missing one. */
/**
 * Gesture clips, authored for this game (`tools/synty-import/build-emotes.mjs`).
 *
 * These replace `CLIP_WAVE`/`CLIP_PUNCH`/`CLIP_SLASH`, which named `Wave`, `Punch_Right` and
 * `Sword_Slash` and were bound to five emotes. Read off the shipped GLBs on 2026-09-21, every
 * one of the fifteen characters carries exactly four clips — `Idle_Gun`, `Walk`, `Run`,
 * `Death` — and none of those three exists in any of them. No emote had ever played a body
 * animation; the header below claiming "the two rig families share exactly three expressive
 * clips" was describing assets this repo does not contain. There are not two rig families
 * either: all fifteen carry the byte-identical 38-bone skeleton, which is why one authored
 * file drives every operator.
 *
 * Six of the twelve get one. The other six stay voice-and-bubble on purpose: "Affirmative" and
 * "That one was on me" have no gesture that tells them apart, and four SKUs sharing a pose is
 * the same thing sold four times — the exact trap this file's header already called out about
 * body poses.
 */
const CLIP_CONTACT = ['Emote_Contact'] as const;
const CLIP_NEED_BACKUP = ['Emote_NeedBackup'] as const;
const CLIP_COVERING = ['Emote_Covering'] as const;
const CLIP_ON_ME = ['Emote_OnMe'] as const;
const CLIP_NICE_SHOT = ['Emote_NiceShot'] as const;
const CLIP_GOOD_GAME = ['Emote_GoodGame'] as const;
const NO_CLIP: readonly string[] = [];

export const EMOTES: Readonly<Record<number, EmoteDefinition>> = {
  [EMOTE.contact]: {
    id: EMOTE.contact,
    art: 'contact',
    name: 'Contact',
    line: 'Contact!',
    description: 'The only word that matters, said fast.',
    clips: CLIP_CONTACT,
    voice: 'contact',
    priceCoins: 600,
    currency: 'PREMIUM',
  },
  [EMOTE.needBackup]: {
    id: EMOTE.needBackup,
    art: 'need-backup',
    name: 'Need Backup',
    line: 'Need backup, now.',
    description: 'For when holding the angle stopped working.',
    clips: CLIP_NEED_BACKUP,
    voice: 'need-backup',
    priceCoins: 700,
    currency: 'PREMIUM',
  },
  [EMOTE.covering]: {
    id: EMOTE.covering,
    art: 'covering',
    name: 'Covering',
    line: "I've got this lane.",
    description: 'Said once, meant for the whole round.',
    clips: CLIP_COVERING,
    voice: 'covering',
    priceCoins: 800,
    currency: 'PREMIUM',
  },
  [EMOTE.onMe]: {
    id: EMOTE.onMe,
    art: 'on-me',
    name: 'On Me',
    line: 'On me — move.',
    description: 'A wave and an order, in that order.',
    clips: CLIP_ON_ME,
    voice: 'on-me',
    priceCoins: 900,
    currency: 'PREMIUM',
  },
  [EMOTE.regroup]: {
    id: EMOTE.regroup,
    art: 'regroup',
    name: 'Regroup',
    line: 'Falling back. Regroup.',
    description: 'Nobody has ever said this early enough.',
    clips: NO_CLIP,
    voice: 'regroup',
    priceCoins: 1000,
    currency: 'PREMIUM',
  },
  [EMOTE.holdingHere]: {
    id: EMOTE.holdingHere,
    art: 'holding-here',
    name: 'Holding Here',
    line: "I'm not moving.",
    description: 'A promise, usually kept for about nine seconds.',
    clips: NO_CLIP,
    voice: 'holding-here',
    priceCoins: 1100,
    currency: 'PREMIUM',
  },
  [EMOTE.niceShot]: {
    id: EMOTE.niceShot,
    art: 'nice-shot',
    name: 'Nice Shot',
    line: 'Hell of a shot.',
    description: 'Costs nothing to say and is never wasted.',
    clips: CLIP_NICE_SHOT,
    voice: 'nice-shot',
    priceCoins: 1200,
    currency: 'PREMIUM',
  },
  [EMOTE.myBad]: {
    id: EMOTE.myBad,
    art: 'my-bad',
    name: 'My Bad',
    line: 'That one was on me.',
    description: 'The rarest line in the game.',
    clips: NO_CLIP,
    voice: 'my-bad',
    priceCoins: 1300,
    currency: 'PREMIUM',
  },
  [EMOTE.goodGame]: {
    id: EMOTE.goodGame,
    art: 'good-game',
    name: 'Good Game',
    line: 'Good game. All of you.',
    description: 'For the end of a round that earned it.',
    clips: CLIP_GOOD_GAME,
    voice: 'good-game',
    priceCoins: 1400,
    currency: 'PREMIUM',
  },
  [EMOTE.affirmative]: {
    id: EMOTE.affirmative,
    art: 'affirmative',
    name: 'Affirmative',
    line: 'Affirmative.',
    description: 'One word, no argument.',
    clips: NO_CLIP,
    voice: 'affirmative',
    priceCoins: 1500,
    currency: 'PREMIUM',
  },
  [EMOTE.standDown]: {
    id: EMOTE.standDown,
    art: 'stand-down',
    name: 'Stand Down',
    line: 'Stand down. It is over.',
    description: 'Said to a room that has already lost.',
    clips: NO_CLIP,
    voice: 'stand-down',
    priceCoins: 1650,
    currency: 'PREMIUM',
  },
  [EMOTE.blackLine]: {
    id: EMOTE.blackLine,
    art: 'black-line',
    name: 'Black Line',
    line: 'Black Line. Every time.',
    description: 'The unit call. Costs the most, says the least.',
    clips: NO_CLIP,
    voice: 'black-line',
    priceCoins: 1800,
    currency: 'PREMIUM',
  },
};

export const EMOTE_LIST: readonly EmoteDefinition[] = Object.values(EMOTES);
export const MAX_EMOTE_ID = Math.max(...EMOTE_LIST.map((emote) => emote.id));

export function isKnownEmote(id: unknown): id is EmoteId {
  return typeof id === 'number' && Number.isInteger(id) && EMOTES[id] !== undefined;
}

/** The `UserItem.itemId` a bought emote owns — one spelling, like `charmItemId`. */
export function emoteItemId(id: number): string {
  return `emote:${id}`;
}

/**
 * Where an emote's voice line is served from.
 *
 * One function, so the importer, the audio preloader and the playback site cannot spell the
 * path three ways. Under `voice/` beside the announcer lines rather than in a directory of its
 * own: both come out of the same generator through the same radio treatment in
 * `tools/sfx/import-generated.mjs`, and a second directory would only invite a second
 * treatment.
 */
/** Where a callout's glyph lives — the visual half of `emoteVoicePath`. */
export function emoteArtPath(art: string): string {
  return `/game/ui/emotes/${art}.png`;
}

export function emoteVoicePath(voice: string): string {
  return `/game/audio/voice/emote-${voice}.ogg`;
}
